import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Minimize2 } from 'lucide-react';
import { Button, Modal, cn, useReducedMotion } from '@ai-tutor/ui';

interface FocusModeLayerProps {
  open: boolean;
  onExit: () => void;
  /**
   * `column`: the classic view, the timer alone on the page's ground.
   * `ambient`: the sakura - the scene fills the screen, opening out of its box.
   */
  variant?: 'column' | 'ambient';
  /** The box the ambient scene expands from, and shrinks back into. */
  fromRect?: () => DOMRect | null | undefined;
  /** A function child gets the layer's own exit, which plays the shrink first. */
  children: ReactNode | ((exit: () => void) => ReactNode);
}

const EXPAND_MS = 700;

const insetOf = (r: DOMRect) =>
  `inset(${r.top}px ${window.innerWidth - r.right}px ${window.innerHeight - r.bottom}px ${r.left}px round 16px)`;

/**
 * Focus mode: the timer alone, over the whole app. Opt-in, from the stage's
 * button, `F`, or the "Start in focus mode" setting; the default page keeps
 * the app one click away, because students move around it mid-block.
 *
 * The shared `Modal` does the portal, Escape and focus return. Focus lands on
 * the layer itself (`data-autofocus`), not its first button, so Space still
 * pauses rather than pressing "Exit". `data-timer-shortcuts` tells the Space
 * and `F` shortcuts that this dialog is the timer, not one on top of it.
 */
export function FocusModeLayer({
  open,
  onExit,
  variant = 'column',
  fromRect,
  children,
}: FocusModeLayerProps) {
  const reduced = useReducedMotion();
  const ambient = variant === 'ambient';
  const frame = useRef<HTMLDivElement>(null);
  const [leaving, setLeaving] = useState(false);

  // The ambient scene opens out of its box on the page, as if the picture
  // were expanding: start clipped to the box, then open to the full screen.
  useLayoutEffect(() => {
    if (!open || !ambient || reduced || !frame.current) return;
    const r = fromRect?.();
    if (!r) return;
    const el = frame.current;
    el.style.transition = 'none';
    el.style.clipPath = insetOf(r);
    void el.getBoundingClientRect();
    const id = requestAnimationFrame(() => {
      el.style.transition = `clip-path ${EXPAND_MS}ms cubic-bezier(.65,0,.35,1)`;
      el.style.clipPath = 'inset(0px 0px 0px 0px round 0px)';
    });
    return () => cancelAnimationFrame(id);
  }, [open, ambient, reduced]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) setLeaving(false);
  }, [open]);

  const exit = () => {
    const r = ambient && !reduced ? fromRect?.() : null;
    if (!r || !frame.current || leaving) return onExit();
    setLeaving(true);
    frame.current.style.clipPath = insetOf(r);
    window.setTimeout(onExit, EXPAND_MS);
  };

  return (
    <Modal
      isOpen={open}
      onClose={exit}
      ariaLabel='Focus mode'
      showCloseButton={false}
      closeOnBackdropClick={false}
      size='full'
      overlayClassName={cn(
        'items-stretch justify-stretch backdrop-blur-none',
        ambient ? 'bg-transparent' : 'animate-focus-mode-in bg-background'
      )}
      className='m-0 h-full max-h-none max-w-none overflow-hidden'
      contentClassName='rounded-none border-0 bg-transparent shadow-none'
    >
      <div
        ref={frame}
        data-autofocus
        data-timer-shortcuts
        data-leaving={leaving || undefined}
        tabIndex={-1}
        className={cn(
          'relative h-full w-full focus:outline-none',
          ambient ? 'overflow-hidden' : 'flex flex-col px-4 pb-6 pt-4 sm:px-8'
        )}
      >
        {ambient ? (
          typeof children === 'function' ? (
            children(exit)
          ) : (
            children
          )
        ) : (
          <>
            <div className='flex items-center justify-between'>
              <Button
                variant='ghost'
                size='sm'
                onClick={exit}
                className='text-muted-foreground max-sm:h-11'
              >
                <Minimize2 className='mr-2 h-4 w-4' aria-hidden='true' />
                Exit focus mode
              </Button>
            </div>
            <div className='flex min-h-0 flex-1 flex-col items-center justify-center py-4'>
              {typeof children === 'function' ? children(exit) : children}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/** `true` after `ms` with no pointer or key input; any input clears it. */
export function useIdle(enabled: boolean, ms = 3000): boolean {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    if (!enabled) {
      setIdle(false);
      return;
    }
    let timer = window.setTimeout(() => setIdle(true), ms);
    const wake = () => {
      setIdle(false);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setIdle(true), ms);
    };
    const events = ['pointermove', 'pointerdown', 'keydown'] as const;
    events.forEach(e => window.addEventListener(e, wake, { passive: true }));
    return () => {
      window.clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, wake));
    };
  }, [enabled, ms]);
  return idle;
}
